'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  User,
  Membership,
  getMe,
  logout as logoutApi,
  refreshToken,
  normalizeMembership,
  membershipFromRedeem,
  DEFAULT_MEMBERSHIP,
  AUTH_CLIENT_STATE_KEY,
  getAuthClientState,
  setAuthClientState,
  shouldAttemptTokenRefresh,
} from '@/lib/auth';

interface AuthContextType {
  user: User | null;
  membership: Membership | null;
  isPremium: boolean;
  isLoading: boolean;
  isLoggedIn: boolean;
  refetch: () => Promise<void>;
  applyMembership: (membership: Membership) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Only fetch user data on client-side
  useEffect(() => {
    setMounted(true);
  }, []);

  const fetchUser = useCallback(async () => {
    // Don't fetch on server-side or before mount
    if (typeof window === 'undefined' || !mounted) {
      setIsLoading(false);
      return;
    }

    const isUnauthorized = (err: unknown) => {
      const msg = String((err as { message?: string })?.message || '');
      return msg === 'Unauthorized' || msg.includes('401');
    };

    /** Misafir / süresi dolmuş oturum — konsola yazma */
    const isExpectedGuestSession = (err: unknown) => {
      const msg = String((err as { message?: string })?.message || '').toLowerCase();
      return (
        isUnauthorized(err) ||
        msg.includes('no refresh token') ||
        msg.includes('refresh token') ||
        msg.includes('jwt expired') ||
        msg.includes('invalid token')
      );
    };

    // Bilinen misafir: /me + /refresh spam’i yok
    if (getAuthClientState() === 'guest') {
      setUser(null);
      setMembership(null);
      setIsLoading(false);
      return;
    }

    try {
      const data = await getMe();
      // Store policy in user object for easy access
      if (data.user && data.policy) {
        (data.user as any).policy = data.policy;
      }
      setUser(data.user);
      setMembership(normalizeMembership(data.membership) ?? DEFAULT_MEMBERSHIP);
      setAuthClientState('user');
    } catch (error: unknown) {
      // Unauthorized → yalnızca oturum ipucu varsa refresh (misafirde 401 gürültüsü olmasın)
      if (isUnauthorized(error)) {
        if (!shouldAttemptTokenRefresh()) {
          setUser(null);
          setMembership(null);
          setAuthClientState('guest');
          return;
        }
        try {
          await refreshToken();
          const data = await getMe();
          if (data.user && data.policy) {
            (data.user as any).policy = data.policy;
          }
          setUser(data.user);
          setMembership(normalizeMembership(data.membership) ?? DEFAULT_MEMBERSHIP);
          setAuthClientState('user');
          return;
        } catch (refreshError) {
          setUser(null);
          setMembership(null);
          setAuthClientState('guest');
          if (!isExpectedGuestSession(refreshError) && process.env.NODE_ENV === 'development') {
            console.warn(
              '[AuthProvider] Token refresh failed:',
              String((refreshError as { message?: string })?.message || refreshError),
            );
          }
          return;
        }
      }

      // Geçici sunucu/ağ hatalarında mevcut user state'ini koru
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[AuthProvider] Error fetching user (non-auth):',
          String((error as { message?: string })?.message || error),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return

    let idleId: number | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const run = () => {
      if (!cancelled) void fetchUser()
    }

    const ric = window.requestIdleCallback?.bind(window)
    if (ric) {
      idleId = ric(run, { timeout: 1800 })
    } else {
      timeoutId = setTimeout(run, 1)
    }

    // Diğer sekmede giriş/çıkış
    const onStorage = (e: StorageEvent) => {
      if (e.key !== AUTH_CLIENT_STATE_KEY) return
      if (e.newValue === 'user') void fetchUser()
      if (e.newValue === 'guest') {
        setUser(null)
        setMembership(null)
      }
    }
    window.addEventListener('storage', onStorage)

    return () => {
      cancelled = true
      window.removeEventListener('storage', onStorage)
      if (idleId != null && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId)
      }
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [mounted, fetchUser]);

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } finally {
      setUser(null);
      setMembership(null);
    }
  }, []);

  const applyMembership = useCallback((next: Membership) => {
    setMembership(normalizeMembership(next) ?? next);
  }, []);

  const isLoggedIn = !!user;
  const isPremium = Boolean(membership?.isPremium);

  return (
    <AuthContext.Provider
      value={{
        user,
        membership,
        isPremium,
        isLoading,
        isLoggedIn,
        refetch: fetchUser,
        applyMembership,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useMe() {
  return useAuth();
}
